import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { AuthState, PageConfig, ViewModeState } from "./types";

/**
 * UniversalEntityPageStaticContext
 *
 * The STABLE half of the page context:
 * - pageConfig: entity name, table config, hooks, form props, custom components
 * - auth: the current user and their capability flags
 * - viewModeState: table / grouped / cards / board, changed only by an explicit
 *   user toggle (so it belongs with the shell, not with the per-keystroke half)
 *
 * Neither changes while the user types in the search box, pages through
 * results, or opens a modal — which is exactly why they live apart from the
 * data/UI/handler contexts. Board views read `pageConfig.config` and
 * `pageConfig.hooks` and nothing else stable-side; before this context existed
 * they took the whole god context and re-rendered on every keystroke.
 */
export interface UniversalEntityPageStaticContextValue<T extends BaseEntity> {
  pageConfig: PageConfig<T>;
  auth: AuthState;
  viewModeState: ViewModeState;
}

const UniversalEntityPageStaticContext = createContext<
  UniversalEntityPageStaticContextValue<BaseEntity> | undefined
>(undefined);

export function UniversalEntityPageStaticProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageStaticContextValue<T>;
}) {
  return (
    <UniversalEntityPageStaticContext.Provider
      value={value as unknown as UniversalEntityPageStaticContextValue<BaseEntity>}
    >
      {children}
    </UniversalEntityPageStaticContext.Provider>
  );
}

/**
 * Non-throwing read. Returns `null` outside a provider — for components (custom
 * headers, the grouping wrapper) that legitimately render both inside and
 * outside an entity page.
 */
export function useOptionalUniversalEntityPageStaticContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageStaticContextValue<T> | null {
  const context = useContext(UniversalEntityPageStaticContext);
  return (context as unknown as UniversalEntityPageStaticContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageStaticContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageStaticContextValue<T> {
  const context = useOptionalUniversalEntityPageStaticContext<T>();
  if (!context) {
    throw new Error(
      "useUniversalEntityPageStaticContext must be used within UniversalEntityPageStaticProvider"
    );
  }
  return context;
}
